import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { supabase } from './utils/supabaseClient';
import SplashScreen from './components/SplashScreen';
import Layout from './Layout';
import { ModalProvider } from './context/ModalContext';
import { MobileHeaderProvider } from './context/MobileHeaderContext';
import Home from './page/Home';
import Browse from './page/Browse';
import Profile from './page/Profile';
import Friends from './page/Friends';
import Events from './page/Events';
import Sports from './page/Sports';
import CreateEvent from './page/CreateEvent';
import EventDetails from './page/EventDetails';
import UserDetail from './page/UserDetail';
import AccountSecurity from './page/AccountSecurity';
import PrivacyPolicy from './page/PrivacyPolicy';
import AboutUs from './page/AboutUs';
import TermsOfService from './page/TermsOfService';
import Login from './page/Login';
import Notifications from './page/Notifications';
import MyEvents from './page/MyEvents';
import TravelMates from './page/TravelMates';
import TravelProfile from './page/TravelProfile';
import TravelPostDetail from './page/TravelPostDetail';
import CreateTravelPost from './page/CreateTravelPost';
import EditTravelPost from './page/EditTravelPost';
import MyTravelMates from './page/MyTravelMates';
import TravelUserDetail from './page/TravelUserDetail';
import LevelAndBadges from './page/LevelAndBadges';
import AboutJoinU from './page/AboutJoinU';
import Quiz from './page/Quiz';

// Admin Pages
import AdminRoute from './components/common/AdminRoute';
import AdminDashboard from './page/admin/AdminDashboard';
import AdminHome from './page/admin/AdminHome';
import AdminReports from './page/admin/AdminReports';
import AdminUsers from './page/admin/AdminUsers';
import AdminBlocked from './page/admin/AdminBlocked';
import AdminEventRecommendations from './page/admin/AdminEventRecommendations';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 3 * 60 * 1000,   // 3 分鐘內不重新請求
      gcTime: 10 * 60 * 1000,     // 10 分鐘後清除快取
      refetchOnWindowFocus: true,  // 切回視窗時背景更新
      retry: 1,
    },
  },
});


export const UserContext = React.createContext({ currentUser: null, session: null, blockedIds: [], setSession: () => { }, setCurrentUser: () => { }, setBlockedIds: () => { } });

// 換頁時自動捲回頂端（SPA 預設會保留上一頁的捲動位置，
// 造成進入隱私權政策等子頁時停在頁面中間）
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AuthGate() {
  const { session } = React.useContext(UserContext);
  const location = useLocation();

  if (!session) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <Outlet />;
}

// Phase 1.96: 未完成初始設定（is_onboarded = false）的帳號，
// 一律導向個人檔案頁完成必填資料（含 18+ 生日驗證）後才能使用其他功能
function OnboardingGate() {
  const { currentUser } = React.useContext(UserContext);
  const location = useLocation();

  const needsOnboarding = currentUser?.profile?.is_onboarded === false;
  if (needsOnboarding && location.pathname !== '/profile') {
    return <Navigate to="/profile" replace />;
  }

  return <Outlet />;
}

function App() {
  const [session, setSession] = useState(null);
  const [isSessionLoading, setIsSessionLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [blockedIds, setBlockedIds] = useState([]);
  const [showSplash, setShowSplash] = useState(true);
  const [isBanned, setIsBanned] = useState(false);

  // 初始化 Supabase Session
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setIsSessionLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      // Keep the previous session object when the user is unchanged:
      // supabase-js emits SIGNED_IN / TOKEN_REFRESHED with a new object on
      // every tab refocus, which would otherwise re-trigger setupUser and
      // unmount the whole app tree (losing form state and open dialogs).
      // Token refresh is handled internally by the supabase client.
      setSession(prev => (prev?.user?.id === session?.user?.id ? prev : session));
    });

    return () => subscription.unsubscribe();
  }, []);

  const setupUser = useCallback(async () => {
    if (!session?.user) return;

    setIsUserLoading(true);
    console.log('[DEBUG] setupUser via Supabase running...');

    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (error) {
        console.error('Fetch profile error:', error);
        setCurrentUser({ _id: session.user.id, id: session.user.id, email: session.user.email, profile: { name: session.user.email, bio: '讀取失敗' } });
      } else if (profile) {
        if (profile.is_banned) {
          setIsBanned(true);
          await supabase.auth.signOut();
          setIsUserLoading(false);
          return;
        }

        setCurrentUser({
          _id: profile.id,
          id: profile.id,
          email: session.user.email, // email 已自 profiles 移除，一律取自 auth session
          profile: profile
        });

        // Fetch blocked users (both directions for mutual invisibility)
        const { data: blockedData } = await supabase
          .from('blocked_users')
          .select('blocker_id, blocked_id')
          .or(`blocker_id.eq.${session.user.id},blocked_id.eq.${session.user.id}`);

        const mutualBlockedIds = (blockedData || []).map(row =>
          row.blocker_id === session.user.id ? row.blocked_id : row.blocker_id
        );
        setBlockedIds(mutualBlockedIds);

        console.log('[DEBUG] User profile fetched from Supabase:', profile);
      }
    } catch (error) {
      console.error('[DEBUG] Error during setupUser:', error);
      setCurrentUser({ _id: session.user.id, id: session.user.id, email: session.user.email, profile: { name: 'Error', photo: '🥲', bio: '網路或後端錯誤' } });
    } finally {
      setIsUserLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (session && !isSessionLoading) {
      setupUser();
    } else if (!isSessionLoading && !session) {
      // 未登入時清空用戶資料
      setIsUserLoading(false);
      setCurrentUser(null);
    }
  }, [session, isSessionLoading, setupUser]);

  if (showSplash) {
    return <SplashScreen onFinish={() => setShowSplash(false)} />;
  }

  if (isBanned) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#fef2f2', padding: '20px', textAlign: 'center' }}>
        <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
          <span style={{ fontSize: '40px' }}>🚫</span>
        </div>
        <h1 style={{ color: '#dc2626', fontSize: '24px', fontWeight: '800', marginBottom: '16px' }}>您的帳號已被停權</h1>
        <p style={{ color: '#7f1d1d', marginBottom: '32px', lineHeight: '1.6' }}>
          您的帳號因違反社群守則，目前已被暫停使用。<br />
          若您認為這是誤判或有任何疑問，請聯繫客服中心：<br />
          <strong>service@joinuworld.com</strong>
        </p>
        <button
          onClick={() => { setIsBanned(false); window.location.href = '/login'; }}
          style={{ padding: '14px 32px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)' }}
        >
          返回登入頁
        </button>
      </div>
    );
  }

  // Only block the UI before the first profile fetch completes; background
  // refetches must not unmount the router (they would wipe all page state)
  if (isSessionLoading || (session && isUserLoading && !currentUser)) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh',
        background: 'linear-gradient(135deg, #4e8bca 0%, #6a9fd3 100%)'
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div style={{
            width: '48px', height: '48px', border: '4px solid rgba(255, 255, 255, 0.3)',
            borderTop: '4px solid white', borderRadius: '50%', margin: '0 auto 16px',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ fontSize: '1.125rem', fontWeight: '500' }}>載入中...</p>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ModalProvider>
          <UserContext.Provider value={{ currentUser, session, blockedIds, setSession, setCurrentUser, setBlockedIds }}>
            <MobileHeaderProvider>
            {/* Web 版掛在 /app 路徑下（/ 由 landing page 使用）；iOS 原生以根路徑載入 */}
            <BrowserRouter basename={Capacitor.isNativePlatform() ? '/' : '/app'}>
              <ScrollToTop />
              <Routes>
                <Route path="/login" element={
                  session ? <Navigate to="/home" replace /> : <Login />
                } />

                {/* 公開頁面：不需要登入即可瀏覽 */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/about" element={<AboutUs />} />

                <Route element={<Layout />}>
                  {/* 公開頁面：允許訪客免登入瀏覽 */}
                  <Route path="/" element={<Navigate to="/home" replace />} />
                  <Route path="/home" element={<Home />} />
                  <Route path="/sports" element={<Sports />} />
                  <Route path="/browse" element={<Browse />} />
                  <Route path="/events" element={<Events />} />
                  <Route path="/events/:id" element={<EventDetails />} />
                  <Route path="/user/:userId" element={<UserDetail />} />
                  <Route path="/travel" element={<TravelMates />} />
                  <Route path="/travel/:id" element={<TravelPostDetail />} />
                  <Route path="/travel/user/:userId" element={<TravelUserDetail />} />
                  <Route path="/levels-badges" element={<LevelAndBadges />} />
                  <Route path="/about-joinu" element={<AboutJoinU />} />
                  <Route path="/quiz" element={<Quiz />} />

                  {/* 需要登入的保護頁面 */}
                  <Route element={<AuthGate />}>
                    <Route element={<OnboardingGate />}>
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/my" element={<Navigate to="/profile" replace />} />
                      <Route path="/account-security" element={<AccountSecurity />} />
                      <Route path="/friends" element={<Friends />} />
                      <Route path="/events/create" element={<CreateEvent mode="event" />} />
                      <Route path="/events/:id/edit" element={<CreateEvent mode="event" />} />
                      <Route path="/sports/create" element={<CreateEvent mode="sport" />} />
                      <Route path="/sports/:id/edit" element={<CreateEvent mode="sport" />} />
                      <Route path="/notifications" element={<Notifications />} />
                      <Route path="/my-events" element={<MyEvents />} />
                      <Route path="/my-sports" element={<MyEvents mode="sport" />} />
                      <Route path="/travel/profile" element={<TravelProfile />} />
                      <Route path="/travel/create" element={<CreateTravelPost />} />
                      <Route path="/travel/edit/:id" element={<EditTravelPost />} />
                      <Route path="/my-travel" element={<MyTravelMates />} />

                      {/* Admin Routes */}
                      <Route element={<AdminRoute />}>
                        <Route path="/admin" element={<AdminDashboard />}>
                          <Route index element={<AdminHome />} />
                          <Route path="reports" element={<AdminReports />} />
                          <Route path="users" element={<AdminUsers />} />
                          <Route path="blocked" element={<AdminBlocked />} />
                          <Route path="event-recommendations" element={<AdminEventRecommendations />} />
                        </Route>
                      </Route>
                    </Route>
                  </Route>

                  <Route path="*" element={<Navigate to="/home" replace />} />
                </Route>
              </Routes>
            </BrowserRouter>
            </MobileHeaderProvider>
          </UserContext.Provider>
        </ModalProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
